// @core/stores/billing/BillingProductStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { billingApi } from '@core/api';
import type {
  CreateBillingProductRequest,
  Product,
  UpdateBillingProductRequest,
} from '@core/types/billing';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class BillingProductStore {
  // List state
  items: Product[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Product | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Product>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Product | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Product | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create product
   */
  async CreateBillingProduct(data: CreateBillingProductRequest): Promise<Product> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.CreateBillingProduct(data);
      const item = response.product;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Get product by ID
   */
  async GetBillingProduct(id: string): Promise<Product> {
    // Check cache first
    const cached = this.cache.get(id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await billingApi.GetBillingProduct(id);
      const item = response.product;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List products
   */
  async ListBillingProducts(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await billingApi.ListBillingProducts(params);

      runInAction(() => {
        this.items = response.products?.products ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update product
   */
  async UpdateBillingProduct(id: string, data: UpdateBillingProductRequest): Promise<Product> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await billingApi.UpdateBillingProduct(id, data);
      const item = response.product;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const billingProductStore = new BillingProductStore();
